import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, TextField, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Chip, InputAdornment, IconButton, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { adminApi } from '../../api';
import { useAuth } from '../../AuthContext';

const EVENT_COLORS = {
  STUDY_ACCESS:         { bg: '#002233', color: '#00bcd4' },
  STUDY_INGEST:         { bg: '#003322', color: '#66bb6a' },
  STUDY_METADATA_EDIT:  { bg: '#2e1a00', color: '#ffb74d' },
  LOGIN:                { bg: '#1a1a2e', color: '#7986cb' },
  LIFECYCLE_TRANSITION: { bg: '#2e1a2e', color: '#ce93d8' },
  DELETE:               { bg: '#2e0e0e', color: '#ef9a9a' },
};

const EVENT_TYPES = [
  '', 'STUDY_ACCESS', 'SERIES_ACCESS', 'INSTANCE_ACCESS',
  'STUDY_INGEST', 'STUDY_DELETE', 'STUDY_METADATA_EDIT',
  'LOGIN', 'LOGOUT', 'AUTH_FAILURE',
  'LIFECYCLE_TRANSITION', 'REHYDRATION_REQUEST', 'EXPORT',
];

const fmtTime = (t) => t ? new Date(t).toLocaleString() : '—';

const AuditLog = () => {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some(role => String(role).toLowerCase() === 'admin');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState('');
  const [studyUid, setStudyUid] = useState('');
  const [username, setUsername] = useState('');
  const [detailsWidth, setDetailsWidth] = useState(420);
  const [resizingDetails, setResizingDetails] = useState(false);

  const load = () => {
    if (!isAdmin) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    adminApi.getAudit({ event_type: eventType || undefined, study_uid: studyUid || undefined, username: username || undefined, limit: 200 })
      .then(setRows).catch(console.error).finally(() => setLoading(false));
  };

  // Reload only when admin eligibility changes; filter changes are applied by Refresh/Enter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [isAdmin]);

  useEffect(() => {
    if (!resizingDetails) return undefined;

    const handleMove = (event) => {
      const table = document.querySelector('[data-audit-table]');
      if (!table) return;
      const rect = table.getBoundingClientRect();
      const nextWidth = Math.max(260, Math.min(1000, rect.right - event.clientX - 8));
      setDetailsWidth(nextWidth);
    };
    const stop = () => setResizingDetails(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stop);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', stop);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingDetails]);

  if (!isAdmin) {
    return (
      <Box>
        <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1, mb: 3 }}>Audit Log</Typography>
        <Alert severity="warning" sx={{ bgcolor: '#1a1400', color: '#ffecb3', border: '1px solid #3d2f00' }}>
          Admin role required to view audit records.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>Audit Log</Typography>
        <IconButton onClick={load} sx={{ color: '#555', '&:hover': { color: 'primary.main' } }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: '#666' }}>Event Type</InputLabel>
          <Select value={eventType} label="Event Type" onChange={e => setEventType(e.target.value)}
            sx={{ borderRadius: 0, color: '#e0e0e0' }}>
            {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t || 'All Events'}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" placeholder="Study UID filter…" value={studyUid}
          onChange={e => setStudyUid(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: '#555' }} /></InputAdornment> }}
          sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& input': { color: '#ccc', fontSize: 12 } }}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <TextField
          size="small" label="Username" placeholder="Username…" value={username}
          onChange={e => setUsername(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: '#555' }} /></InputAdornment> }}
          sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiInputLabel-root': { color: '#666' }, '& input': { color: '#ccc', fontSize: 12 } }}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1, overflowX: 'auto' }}>
          <Table size="small" data-audit-table sx={{ tableLayout: 'fixed', minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                {[
                  ['Time', 170], ['Event', 155], ['Patient ID', 140], ['Study UID', 210], ['User', 170], ['Source AE', 120],
                ].map(([h, w]) => (
                  <TableCell key={h} sx={{ width: w, bgcolor: '#0d0d0d', color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, borderBottom: '1px solid #222' }}>
                    {h.toUpperCase()}
                  </TableCell>
                ))}
                <TableCell sx={{ width: detailsWidth, bgcolor: '#0d0d0d', color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, borderBottom: '1px solid #222', position: 'relative' }}>
                  DETAILS
                  <Box
                    title="Drag to resize details column"
                    onMouseDown={(event) => { event.preventDefault(); setResizingDetails(true); }}
                    sx={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize',
                      borderLeft: resizingDetails ? '2px solid #00bcd4' : '1px solid #333',
                      '&:hover': { borderLeft: '2px solid #00bcd4' },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', color: '#555', py: 6 }}>No audit records found</TableCell>
                </TableRow>
              )}
              {rows.map(r => {
                const clr = EVENT_COLORS[r.event_type] || { bg: '#1a1a1a', color: '#aaa' };
                return (
                  <TableRow key={r.id} sx={{ '&:hover': { bgcolor: '#141414' } }}>
                    <TableCell sx={{ color: '#666', fontSize: 11, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' }}>
                      {fmtTime(r.event_time)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                      <Chip label={r.event_type} size="small"
                        sx={{ ...clr, borderRadius: 0, fontSize: 10, height: 18 }} />
                    </TableCell>
                    <TableCell sx={{ color: '#aaa', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>
                      {r.patient_id || '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: 11, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a', maxWidth: 180 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.study_uid || '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#666', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>
                      {r.details?.username || r.username || r.user_id || '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#666', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>
                      {r.source_ae || '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#777', fontSize: 11, borderBottom: '1px solid #1a1a1a', width: detailsWidth, maxWidth: detailsWidth, verticalAlign: 'top' }}>
                      {r.details ? <Box
                        component="pre"
                        sx={{
                          m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          maxHeight: 140, overflow: 'auto', pr: 1, color: '#aaa',
                        }}
                      >
                        {JSON.stringify(r.details, null, 2)}
                      </Box> : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AuditLog;
